import React, { useMemo, useState, useEffect } from "react";

import { useFlags } from "launchdarkly-react-client-sdk";
import { useTranslation } from "react-i18next";
import { HorizontalScrollFilters } from "@local-logic/design-system";
import { Map } from "../Map";
import * as styles from "./styles";
import { useNeighborhoodMap } from "~/sdks/NeighborhoodMap/context";
import { useTracking } from "~/tracking";

import { Attribution } from "~/components/Attribution";

import { icons as poiIcons } from "~/components/PoiIcon";
import { categories } from "~/sdks/NeighborhoodMap/constants";

import {
  type POICategory,
  TrackingPipelineEvents,
  AnalyticsEvents,
} from "~/sdks/NeighborhoodMap/types";

export const Content = () => {
  const { t } = useTranslation(["neighborhoodMap", "common"]);
  const flags = useFlags();
  const tracking = useTracking();

  const {
    locale,
    handleMarkerClick,
    activePoiCategory,
    onSelectPoiCategory,
    hideAttribution,
  } = useNeighborhoodMap();
  const [dialogSelectedPoiCategory, setDialogSelectedPoiCategory] = useState<
    POICategory | undefined
  >();

  useEffect(() => {
    handleMarkerClick(undefined);
    setDialogSelectedPoiCategory(activePoiCategory);
  }, [activePoiCategory]);

  const poiCategories = useMemo(
    () =>
      categories.map((category) => ({
        key: category as POICategory,
        icon: poiIcons[category],
      })),
    [flags],
  );

  const handleOnSelectCategory = (category: POICategory) => {
    onSelectPoiCategory(category);

    tracking.postEvent({
      eventType: TrackingPipelineEvents.CATEGORY_CLICKED,
      eventAttributes: [
        {
          label: "category",
          data: category,
        },
      ],
    });

    tracking.trackAnalyticsEvent({
      eventName: AnalyticsEvents.CATEGORY_CLICKED,
      eventAttributes: {
        ll_category: category,
      },
    });
  };

  const handleOnDialogSelectCategory = (category: POICategory) => {
    setDialogSelectedPoiCategory(
      dialogSelectedPoiCategory === category ? undefined : category,
    );
  };

  const handleResetFilters = () => {
    onSelectPoiCategory(undefined);
  };

  const handleApplyFilter = () => {
    onSelectPoiCategory(dialogSelectedPoiCategory);
  };

  return (
    <div className={styles.container}>
      <HorizontalScrollFilters.Root>
        <div className={styles.header}>
          <HorizontalScrollFilters.ScrollList
            activeFilter={activePoiCategory}
            leftArrowAriaLabel={t("filters.navigation.scrollLeftAriaLabel")}
            rightArrowAriaLabel={t("filters.navigation.scrollRightAriaLabel")}
          >
            {poiCategories.map(({ key, icon }) => (
              <HorizontalScrollFilters.Filter<POICategory>
                key={`category-${key}`}
                filter={key}
                label={t(`common:pois.${key}`)}
                icon={icon}
                isActive={key === activePoiCategory}
                onSelectFilter={handleOnSelectCategory}
              />
            ))}
          </HorizontalScrollFilters.ScrollList>
          <HorizontalScrollFilters.DialogTrigger
            label={t("filters.navigation.allFiltersAriaLabel")}
          />
        </div>

        <HorizontalScrollFilters.Dialog
          title={t("filters.popup.title")}
          description={t("filters.popup.description")}
          applyLabel={t("filters.popup.cta.apply")}
          resetLabel={t("filters.popup.cta.reset")}
          onReset={handleResetFilters}
          onApply={handleApplyFilter}
        >
          {poiCategories.map(({ key, icon }) => (
            <HorizontalScrollFilters.Filter<POICategory>
              key={`dialog-category-${key}`}
              filter={key}
              label={t(`common:pois.${key}`)}
              icon={icon}
              isActive={key === dialogSelectedPoiCategory}
              onSelectFilter={handleOnDialogSelectCategory}
            />
          ))}
        </HorizontalScrollFilters.Dialog>
      </HorizontalScrollFilters.Root>

      <Map />

      <Attribution
        sdkName="neighborhood-map"
        locale={locale}
        isHidden={hideAttribution}
      />
    </div>
  );
};
